import { useEffect, useRef } from 'react';
import { YoutubePlayer } from 'Shared/youtube-player';

/**
 * Post-process a container of rendered wiki HTML (`*_rendered` from the API) so
 * the raw markup behaves correctly inside the embed's third-party iframe. React
 * injects that HTML verbatim, so the two things the vanilla did to it have to
 * happen here instead:
 *
 * - **Videos**: hydrate `.youtube-player` placeholders into click-to-play embeds
 *   (vanilla: `YoutubePlayer.init()` in embed.v5.intro.js).
 * - **Links**: force `target="_blank"`. The embed is iframed, so an untargeted
 *   link navigates the embed itself away instead of opening a tab (vanilla:
 *   PrepareEmbed sets target on every `a:not(.no-blank)`).
 *
 * Scoped to the returned ref rather than a global document scan, and guarded so
 * a re-render never double-processes (no duplicate play buttons).
 *
 * `deps` should change whenever the container's HTML changes, so freshly
 * rendered content gets processed.
 */
export function useWikiHtml<T extends HTMLElement>(deps: unknown) {
   const ref = useRef<T>(null);
   useEffect(() => {
      const root = ref.current;
      if (!root) {
         return;
      }

      for (const el of Array.from(root.querySelectorAll<HTMLElement>(YoutubePlayer.elementClass))) {
         if (el.dataset.ytHydrated) {
            continue;
         }
         el.dataset.ytHydrated = '1';
         new YoutubePlayer(el);
      }

      for (const link of Array.from(root.querySelectorAll<HTMLAnchorElement>('a:not(.no-blank)'))) {
         link.target = '_blank';
         link.rel = 'noreferrer';
      }
   }, [deps]);
   return ref;
}
